import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Target, Award, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import './StaticPages.css';

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="static-page">
      <SEO 
        title="About RazChatz - Modern Secure Chat Platform"
        description="Learn about RazChatz, a modern, secure, and lightning-fast chat platform connecting people globally. Discover our mission, values, and what makes us different."
        keywords="about razchatz, chat platform, secure messaging, real-time chat, Mohammad Razak A, international chat, mobile chat app, about us"
        canonical="https://razchatz.netlify.app/about"
        ogTitle="About RazChatz - Connecting People Worldwide"
        ogDescription="RazChatz is revolutionizing online communication with secure, fast, and user-friendly messaging. Learn about our mission and values."
      />
      <div className="static-page-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
      </div>

      <div className="static-page-container">
        <h1 className="static-page-title">About RazChatz</h1>
        
        <div className="static-page-content">
          <section className="about-intro">
            <p className="lead-text">
              RazChatz is a modern, secure, and lightning-fast chat platform designed to connect people 
              across the globe. Built with cutting-edge technology and a focus on user experience, 
              we're revolutionizing the way people communicate online.
            </p>
          </section>

          <section className="about-section">
            <div className="about-icon-wrapper">
              <Target className="about-icon" />
            </div>
            <h2>Our Mission</h2>
            <p>
              Our mission is to provide a seamless, secure, and enjoyable communication platform that 
              brings people together. We believe in creating meaningful connections through technology 
              while respecting user privacy and data security.
            </p>
          </section>

          <section className="about-section">
            <div className="about-icon-wrapper">
              <Award className="about-icon" />
            </div>
            <h2>What Makes Us Different</h2>
            <ul className="feature-list">
              <li><strong>Lightning Fast:</strong> Real-time messaging with zero lag across all devices</li>
              <li><strong>Secure & Private:</strong> End-to-end encryption for all conversations</li>
              <li><strong>Mobile First:</strong> Optimized for smartphones and tablets</li>
              <li><strong>International:</strong> Connect with users worldwide through chat rooms</li>
              <li><strong>Guest Friendly:</strong> Start chatting immediately without registration</li>
              <li><strong>User Control:</strong> Complete preference settings for your experience</li>
            </ul>
          </section>

          <section className="about-section">
            <div className="about-icon-wrapper">
              <Users className="about-icon" />
            </div>
            <h2>Our Community</h2>
            <p>
              RazChatz is more than just a chat platform—it's a global community. With users from 
              around the world, we facilitate connections that transcend borders. Whether you're 
              looking to meet new people, reconnect with friends, or join international chat rooms, 
              RazChatz provides the perfect environment.
            </p>
          </section>

          <section className="about-section">
            <div className="about-icon-wrapper">
              <Heart className="about-icon" />
            </div>
            <h2>Our Values</h2>
            <div className="values-grid">
              <div className="value-card">
                <h3>Privacy First</h3>
                <p>Your conversations are yours. We use end-to-end encryption and never sell your data.</p>
              </div>
              <div className="value-card">
                <h3>Innovation</h3>
                <p>We continuously improve our platform with the latest technology and user feedback.</p>
              </div>
              <div className="value-card">
                <h3>Inclusivity</h3>
                <p>Everyone is welcome. We create a safe space for people from all backgrounds.</p>
              </div>
              <div className="value-card">
                <h3>Reliability</h3>
                <p>24/7 uptime and global support ensure you're always connected when you need to be.</p>
              </div>
            </div>
          </section>

          <section className="about-section">
            <h2>The Technology</h2>
            <p>
              Built with React, Node.js, and Socket.io, RazChatz leverages modern web technologies 
              to deliver a fast, responsive, and reliable experience. Our infrastructure is designed 
              for scalability and performance, ensuring smooth communication even during peak usage.
            </p>
          </section>

          <section className="about-section">
            <h2>Created By</h2>
            <p>
              RazChatz was created by <strong>Mohammad Razak A</strong>, a passionate developer 
              dedicated to building innovative solutions that enhance digital communication. With 
              a focus on user experience and technical excellence, RazChatz represents the future 
              of online messaging.
            </p>
          </section>

          <section className="cta-section">
            <h2>Join Us Today</h2>
            <p>
              Experience the future of communication. Join thousands of users already enjoying 
              RazChatz's seamless messaging experience.
            </p>
            <div className="cta-buttons">
              <button className="cta-button primary" onClick={() => navigate('/signup')}>
                Get Started
              </button>
              <button className="cta-button secondary" onClick={() => navigate('/contact')}>
                Contact Us
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
